import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { platformCoreNetworkApi } from '../../../api/platform/coreNetwork';
import { useAsyncData } from '../hooks/useAsyncData';
import { ConfirmDialog, DataTable, EmptyState, ErrorState, FeedbackBanner, InlineActions, KeyValueGrid, LoadingState, MvpCard, MvpTalentLayout, StatusBadge } from '../components/MvpTalentUi';
import { formatDateTime } from '../utils/format';

const WorkDetailPage: React.FC = () => {
  const { requestId = '' } = useParams();
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [note, setNote] = useState('');
  const { data, loading, error, reload } = useAsyncData(async (signal) => {
    const [workRequest, notifications] = await Promise.all([
      platformCoreNetworkApi.getWorkRequest(requestId, { signal }),
      platformCoreNetworkApi.listNotificationDeliveries({ workRequestId: requestId, limit: 10, page: 1 }, { signal }),
    ]);
    return { workRequest, notifications };
  }, [requestId]);

  return (
    <MvpTalentLayout title={data?.workRequest.title || `Work Request #${requestId}`} subtitle="Platform support, notification visibility, and lifecycle-aware work oversight.">
      {feedback ? <FeedbackBanner tone={feedback.tone} message={feedback.message} onDismiss={() => setFeedback(null)} /> : null}
      {loading ? <LoadingState label="Loading work request…" /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <MvpCard title="Request summary">
            <KeyValueGrid items={[
              { label: 'Instance', value: data.workRequest.instanceName || data.workRequest.instanceId },
              { label: 'Order / project', value: data.workRequest.orderId || '—' },
              { label: 'Request type', value: data.workRequest.requestType },
              { label: 'Provider', value: data.workRequest.fulfillmentProvider },
              { label: 'Status', value: <StatusBadge status={data.workRequest.status} /> },
              { label: 'Assignment', value: <StatusBadge status={data.workRequest.assignment?.status || 'unassigned'} /> },
              { label: 'Talent', value: data.workRequest.assignment?.member?.displayName || 'Unassigned' },
              { label: 'Due date', value: formatDateTime(data.workRequest.deadlineAt) },
              { label: 'Priority', value: data.workRequest.priority || '—' },
              { label: 'Last activity', value: formatDateTime(data.workRequest.lastActivityAt) },
            ]} />
            <p>{data.workRequest.instructions || data.workRequest.description || 'No additional request instructions were returned by the platform API.'}</p>
            <InlineActions>
              {data.workRequest.instanceId ? <Link to={`/command-center/mvp-talent/instances/${data.workRequest.instanceId}`}>Open instance</Link> : null}
              <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setCancelOpen(true)}>Cancel request</button>
            </InlineActions>
          </MvpCard>

          <MvpCard title="Support note">
            <textarea className="mvp-field" style={{ minHeight: 120 }} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Internal support note visible only through Elite Core platform tooling." />
            <InlineActions>
              <button
                type="button"
                className="mvp-button"
                disabled={!note.trim()}
                onClick={async () => {
                  try {
                    await platformCoreNetworkApi.addSupportNote(requestId, { messageBody: note.trim() });
                    setFeedback({ tone: 'success', message: 'Support note added.' });
                    setNote('');
                    await reload();
                  } catch (noteError: any) {
                    setFeedback({ tone: 'error', message: noteError?.message || 'Unable to add the support note.' });
                  }
                }}
              >
                Add internal note
              </button>
            </InlineActions>
          </MvpCard>

          <MvpCard title="Notifications">
            {data.notifications.rows.length ? (
              <DataTable columns={['Delivery', 'Type', 'Recipient', 'Status', 'Created']}>
                {data.notifications.rows.map((delivery) => (
                  <tr key={delivery.id}>
                    <td>#{delivery.id}</td>
                    <td>{delivery.notificationType}</td>
                    <td>{delivery.recipientEmail || delivery.recipientMemberId || '—'}</td>
                    <td><StatusBadge status={delivery.status} /></td>
                    <td>{formatDateTime(delivery.createdAt)}</td>
                  </tr>
                ))}
              </DataTable>
            ) : <EmptyState title="No notification events" message="No related notification deliveries were returned for this request." />}
          </MvpCard>
        </>
      ) : null}
      <ConfirmDialog
        open={cancelOpen}
        title="Cancel work request"
        description="Cancellation runs through the protected platform lifecycle endpoint and records a platform audit event."
        confirmLabel="Cancel request"
        confirmTone="danger"
        onClose={() => setCancelOpen(false)}
        onConfirm={async ({ reason }) => {
          try {
            await platformCoreNetworkApi.cancelWorkRequest(requestId, reason);
            setFeedback({ tone: 'success', message: 'Work request cancelled.' });
            await reload();
          } catch (cancelError: any) {
            setFeedback({ tone: 'error', message: cancelError?.message || 'Unable to cancel the work request.' });
            throw cancelError;
          }
        }}
      />
    </MvpTalentLayout>
  );
};

export default WorkDetailPage;
